/* src/components/hooks/useSessionManager.js - Última Modificación: 03-01-2026 */
/* --------------- único reloj de vida de la sesión -------------------------- */

import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../../context/AuthContext.jsx';

export default function useSessionManager(isAuthenticated) {
  const intervalRef = useRef(null);
  const isRenewingRef = useRef(false);
  const sessionIdRef = useRef(0);

  const [sessionStatus, setSessionStatus] = useState('active');
  const [remainingTime, setRemainingTime] = useState(null);

  const { expireSession } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL;

  /* 🧹 CLEAR TIMER */
  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /* ⏱️ TIMER PRINCIPAL */
  const startTimer = useCallback(
    (token) => {
      clearTimer();

      const sessionId = ++sessionIdRef.current;

      intervalRef.current = setInterval(() => {
        if (sessionId !== sessionIdRef.current) {
          clearInterval(intervalRef.current);
          return;
        }

        try {
          const { exp } = jwtDecode(token);
          const now = Math.floor(Date.now() / 1000);
          const remaining = exp - now;

          if (remaining <= 0) {
            clearTimer();
            setSessionStatus('expired');
            expireSession();
            return;
          }

          setRemainingTime(remaining);

          if (remaining <= 120) {
            setSessionStatus('warning');
          } else {
            setSessionStatus('active');
          }
        } catch {
          clearTimer();
          setSessionStatus('expired');
          expireSession();
        }
      }, 1000);
    },
    [clearTimer, expireSession]
  );

  /* 🔁 RENOVAR SESIÓN */
  const renewSession = useCallback(async () => {
    if (isRenewingRef.current) return;
    isRenewingRef.current = true;

    try {
      const token = sessionStorage.getItem('authToken');
      if (!token) throw new Error('No token');

      const { data } = await axios.post(
        `${apiUrl}/auth/renew-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      sessionStorage.setItem('authToken', data.token);

      setSessionStatus('active');
      setRemainingTime(null);
      startTimer(data.token);
    } catch {
      setSessionStatus('expired');
      expireSession();
    } finally {
      isRenewingRef.current = false;
    }
  }, [apiUrl, expireSession, startTimer]);

  /* 🚀 INIT */
  useEffect(() => {
    if (!isAuthenticated) return;

    const token = sessionStorage.getItem('authToken');
    if (!token) return;

    startTimer(token);
    return clearTimer;
  }, [isAuthenticated, startTimer, clearTimer]);

  return {
    sessionStatus,
    remainingTime,
    renewSession,
  };
}
